import { useEffect, useMemo, useRef, useState } from "react";
import {
  type VstsAccount,
  type VstsBoardColumn,
  type VstsBoardModeConfiguration,
  type VstsConfiguration,
  type VstsIdentityField,
  type VstsProcessWorkItemType,
  type VstsProject,
  type VstsStatusModeConfiguration,
  type VstsTeam,
  type VstsTeamBoard,
  type VstsWorkItemState,
  type VstsWorkItemTypeMapping,
} from "./VstsSourceSettings.types";
import { VstsSourceSettingsApi } from "./VstsSourceSettings.Api";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type { SourceSettingsModel } from "../source-settings/SourceSettingsApiAccess";
import type { VstsSourceSettingsViewModel } from "./VstsSourceSettings.ViewModel.types";
import type { SourceStatusMapping } from "../SourceStatusMapping.types";
import {
  findVstsColumnByKey,
  getVstsColumnKey,
  getVstsColumnOptions,
  getVstsStatusMappingColumnOptions,
  reconcileVstsColumns,
} from "./VstsSourceSettings.columns";

// ─── Constants ────────────────────────────────────────────────────────────────

const NONE_OPTION = "__none";
const BOARD_MODE = "BoardMode";
const STATUS_MODE = "StatusMode";
const DEFAULT_ASSIGNED_TO_FIELD = "System.AssignedTo";

const getColumns = (cfg: Partial<VstsConfiguration>): VstsBoardColumn[] => {
  const raw = cfg.boardMode?.teamBoardDetails?.columns;
  return Array.isArray(raw) ? (raw as VstsBoardColumn[]) : [];
};

const getWorkItemTypeNames = (cfg: Partial<VstsConfiguration>): string[] => {
  const cols = getColumns(cfg);
  if (cols.length === 0) return [];
  const mappings = cols[0].stateMappings;
  return mappings ? Object.keys(mappings) : [];
};

const clearBoardSelection = (
  existing?: Partial<VstsBoardModeConfiguration>,
): Partial<VstsBoardModeConfiguration> => ({
  ...existing,
  teamBoard: undefined,
  teamBoardDetails: undefined,
  columnsToLoad: [],
  doneColumn: undefined,
  workItemTypes: [],
  // Board-mode mappings are keyed by column name, so they are meaningless once
  // the columns they referenced are gone.
  statusMappings: undefined,
});

/** Columns a newly selected board starts with: all but the exit column. */
const getDefaultColumnsToLoad = (
  columns: VstsBoardColumn[],
): VstsBoardColumn[] => columns.filter((c) => c.columnType !== "outgoing");

const getDefaultDoneColumn = (
  columns: VstsBoardColumn[],
): VstsBoardColumn | undefined =>
  columns.find((c) => c.columnType === "outgoing");

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useVstsSourceSettingsViewModel = (
  model: SourceSettingsModel,
): VstsSourceSettingsViewModel => {
  const [accounts, setAccounts] = useState<VstsAccount[]>([]);
  const [projects, setProjects] = useState<VstsProject[]>([]);
  const [teams, setTeams] = useState<VstsTeam[]>([]);
  const [boards, setBoards] = useState<VstsTeamBoard[]>([]);
  const [identityFields, setIdentityFields] = useState<VstsIdentityField[]>([]);
  const [statusWorkItemTypes, setStatusWorkItemTypes] = useState<
    VstsProcessWorkItemType[]
  >([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [isLoadingBoards, setIsLoadingBoards] = useState(false);
  const [isLoadingIdentityFields, setIsLoadingIdentityFields] = useState(false);
  const [isLoadingStatusWorkItemTypes, setIsLoadingStatusWorkItemTypes] =
    useState(false);
  const [hasError, setHasError] = useState(false);
  const [accountQuery, setAccountQuery] = useState("");
  const [projectQuery, setProjectQuery] = useState("");
  const [teamQuery, setTeamQuery] = useState("");
  const [boardQuery, setBoardQuery] = useState("");
  const [statusModeProjectsQuery, setStatusModeProjectsQuery] = useState("");
  const [workItemTypeFilterQuery, setWorkItemTypeFilterQuery] = useState("");
  const [columnsToLoadQuery, setColumnsToLoadQuery] = useState("");
  const [doneColumnQuery, setDoneColumnQuery] = useState("");
  const [identityFieldQuery, setIdentityFieldQuery] = useState("");
  const editingConnectionRef = useRef(model.editingConnection);
  const onUpdateRef = useRef(model.onUpdate);

  useEffect(() => {
    editingConnectionRef.current = model.editingConnection;
    onUpdateRef.current = model.onUpdate;
  }, [model.editingConnection, model.onUpdate]);

  const isVisible =
    model.editingConnection.isAuthorised &&
    model.editingConnection.sourceTypeId.toLowerCase() === SourceTypeIds.Vsts;

  const configuration = useMemo(
    () =>
      parseConnectionConfiguration<VstsConfiguration>(
        model.editingConnection.configuration,
      ),
    [model.editingConnection.configuration],
  );
  const configurationRef = useRef(configuration);

  useEffect(() => {
    configurationRef.current = configuration;
  }, [configuration]);

  // ─── Derived config state ─────────────────────────────────────────────────

  const selectedAccountName = useMemo(
    () => configuration.account?.accountName ?? NONE_OPTION,
    [configuration],
  );
  const selectedMode = useMemo(
    () => configuration.mode ?? BOARD_MODE,
    [configuration],
  );
  const selectedProjectId = useMemo(
    () => configuration.boardMode?.project?.id ?? NONE_OPTION,
    [configuration],
  );
  const selectedTeamId = useMemo(
    () => configuration.boardMode?.team?.id ?? NONE_OPTION,
    [configuration],
  );
  const selectedBoardId = useMemo(
    () => configuration.boardMode?.teamBoard?.id ?? NONE_OPTION,
    [configuration],
  );
  const columns = useMemo(() => getColumns(configuration), [configuration]);
  const workItemTypeNames = useMemo(
    () => getWorkItemTypeNames(configuration),
    [configuration],
  );
  const columnOptions = useMemo(() => getVstsColumnOptions(columns), [columns]);
  const selectedColumnsToLoad = useMemo(() => {
    const keys = (configuration.boardMode?.columnsToLoad ?? []).map(
      getVstsColumnKey,
    );
    // Turning a column's split on or off in Azure DevOps changes which keys the
    // board offers, leaving stored keys that match no column. The multi-select
    // renders its tags from the options, so such a key would be an invisible
    // selection the user cannot see or remove. Only report what the board has;
    // with no columns loaded there is nothing to validate against.
    if (columns.length === 0) {
      return keys;
    }
    const availableKeys = new Set(columns.map(getVstsColumnKey));
    return keys.filter((key) => availableKeys.has(key));
  }, [configuration, columns]);
  const statusMappingColumns = useMemo(
    () =>
      getVstsStatusMappingColumnOptions(
        columns.filter((c) =>
          selectedColumnsToLoad.includes(getVstsColumnKey(c)),
        ),
      ),
    [columns, selectedColumnsToLoad],
  );
  const selectedDoneColumn = useMemo(
    () => configuration.boardMode?.doneColumn ?? undefined,
    [configuration],
  );
  const selectedDoneColumnKey = useMemo(
    () =>
      selectedDoneColumn ? getVstsColumnKey(selectedDoneColumn) : NONE_OPTION,
    [selectedDoneColumn],
  );
  const selectedDoneColumnLabel = useMemo(
    () =>
      selectedDoneColumn
        ? (selectedDoneColumn.displayName ??
          selectedDoneColumn.name ??
          selectedDoneColumn.id ??
          "")
        : "",
    [selectedDoneColumn],
  );
  const selectedWorkItemTypeFilter = useMemo(
    () => configuration.boardMode?.workItemTypes ?? [],
    [configuration],
  );
  const selectedIdentityFieldReferenceName = useMemo(
    () => configuration.assignedToField?.referenceName ?? NONE_OPTION,
    [configuration],
  );
  const statusModeProjects = useMemo(
    () => configuration.statusMode?.projects ?? [],
    [configuration],
  );
  const statusModeProjectIdsKey = useMemo(
    () =>
      statusModeProjects
        .map((project) => project.id ?? "")
        .filter(Boolean)
        .join("|"),
    [statusModeProjects],
  );
  const workItemTypeMappings = useMemo(
    () => configuration.statusMode?.workItemTypeMappings ?? [],
    [configuration],
  );

  const vstsApi = useMemo(
    () =>
      model.sourceSettingsApi?.tryGetSourceApi<VstsSourceSettingsApi>(
        SourceTypeIds.Vsts,
      ) ?? null,
    [model.sourceSettingsApi],
  );

  // ─── Effects ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const load = async (): Promise<void> => {
      const editingConnection = editingConnectionRef.current;
      if (!isVisible || !model.boardId || !editingConnection.id || !vstsApi) {
        setAccounts([]);
        return;
      }
      setIsLoadingAccounts(true);
      setHasError(false);
      try {
        const response = await vstsApi.getAccounts(
          model.boardId,
          editingConnection.id,
        );
        const filtered = response.filter((a) => Boolean(a.accountName));
        setAccounts(filtered);
        const current = configurationRef.current;
        if (!current.account && filtered.length > 0) {
          updateConnectionConfiguration<VstsConfiguration>(
            editingConnection,
            onUpdateRef.current,
            (cfg) =>
              cfg.account
                ? cfg
                : {
                    ...cfg,
                    mode: cfg.mode ?? BOARD_MODE,
                    account: filtered[0],
                    boardMode: cfg.boardMode ?? {},
                  },
            { isDefault: true },
          );
        }
      } catch {
        setAccounts([]);
        setHasError(true);
      } finally {
        setIsLoadingAccounts(false);
      }
    };
    void load();
  }, [isVisible, model.boardId, vstsApi]);

  useEffect(() => {
    const load = async (): Promise<void> => {
      const editingConnection = editingConnectionRef.current;
      if (
        !isVisible ||
        !model.boardId ||
        !editingConnection.id ||
        !vstsApi ||
        selectedAccountName === NONE_OPTION
      ) {
        setProjects([]);
        return;
      }
      setIsLoadingProjects(true);
      setHasError(false);
      try {
        const response = await vstsApi.getProjects(
          model.boardId,
          editingConnection.id,
          selectedAccountName,
        );
        setProjects(response);
      } catch {
        setProjects([]);
        setHasError(true);
      } finally {
        setIsLoadingProjects(false);
      }
    };
    void load();
  }, [isVisible, model.boardId, selectedAccountName, vstsApi]);

  useEffect(() => {
    const load = async (): Promise<void> => {
      const editingConnection = editingConnectionRef.current;
      if (
        !isVisible ||
        !model.boardId ||
        !editingConnection.id ||
        !vstsApi ||
        selectedAccountName === NONE_OPTION
      ) {
        setIdentityFields([]);
        return;
      }
      setIsLoadingIdentityFields(true);
      setHasError(false);
      try {
        const response = await vstsApi.getIdentityFields(
          model.boardId,
          editingConnection.id,
          selectedAccountName,
        );
        setIdentityFields(response);
        const current = configurationRef.current;
        if (!current.assignedToField) {
          const defaultField = response.find(
            (f) => f.referenceName === DEFAULT_ASSIGNED_TO_FIELD,
          );
          if (defaultField) {
            updateConnectionConfiguration<VstsConfiguration>(
              editingConnection,
              onUpdateRef.current,
              (cfg) =>
                cfg.account?.accountName !== selectedAccountName ||
                cfg.assignedToField
                  ? cfg
                  : { ...cfg, assignedToField: defaultField },
              { isDefault: true },
            );
          }
        }
      } catch {
        setIdentityFields([]);
        setHasError(true);
      } finally {
        setIsLoadingIdentityFields(false);
      }
    };
    void load();
  }, [isVisible, model.boardId, selectedAccountName, vstsApi]);

  useEffect(() => {
    const load = async (): Promise<void> => {
      const editingConnection = editingConnectionRef.current;
      if (
        !isVisible ||
        !model.boardId ||
        !editingConnection.id ||
        !vstsApi ||
        selectedAccountName === NONE_OPTION ||
        selectedProjectId === NONE_OPTION
      ) {
        setTeams([]);
        return;
      }
      setIsLoadingTeams(true);
      setHasError(false);
      try {
        const response = await vstsApi.getTeams(
          model.boardId,
          editingConnection.id,
          selectedAccountName,
          selectedProjectId,
        );
        setTeams(response);
        const current = configurationRef.current;
        const existingTeamId = current.boardMode?.team?.id;
        const teamInList = existingTeamId
          ? response.find((t) => t.id === existingTeamId)
          : undefined;
        if (!teamInList) {
          const pick =
            response.length === 1
              ? response[0]
              : response.find((t) => t.isDefault);
          if (pick) {
            updateConnectionConfiguration<VstsConfiguration>(
              editingConnection,
              onUpdateRef.current,
              (cfg) => ({
                ...cfg,
                boardMode: {
                  ...(cfg.boardMode ?? {}),
                  team: pick,
                  ...clearBoardSelection(),
                },
              }),
            );
          }
        }
      } catch {
        setTeams([]);
        setHasError(true);
      } finally {
        setIsLoadingTeams(false);
      }
    };
    void load();
  }, [
    isVisible,
    model.boardId,
    selectedAccountName,
    selectedProjectId,
    vstsApi,
  ]);

  useEffect(() => {
    const load = async (): Promise<void> => {
      const editingConnection = editingConnectionRef.current;
      if (
        !isVisible ||
        !model.boardId ||
        !editingConnection.id ||
        !vstsApi ||
        selectedAccountName === NONE_OPTION ||
        selectedProjectId === NONE_OPTION ||
        selectedTeamId === NONE_OPTION
      ) {
        setBoards([]);
        return;
      }
      setIsLoadingBoards(true);
      setHasError(false);
      try {
        const response = await vstsApi.getTeamBoards(
          model.boardId,
          editingConnection.id,
          selectedAccountName,
          selectedProjectId,
          selectedTeamId,
        );
        setBoards(response);
        const current = configurationRef.current;
        const existingBoardId = current.boardMode?.teamBoard?.id;
        const boardInList = existingBoardId
          ? response.find((b) => b.id === existingBoardId)
          : undefined;
        if (!boardInList && response.length === 1) {
          const pick = response[0];
          try {
            const details = await vstsApi.getTeamBoardDetails(
              model.boardId,
              editingConnection.id,
              selectedAccountName,
              selectedProjectId,
              selectedTeamId,
              pick.id,
            );
            const rawCols = (details.columns ?? []) as VstsBoardColumn[];
            updateConnectionConfiguration<VstsConfiguration>(
              editingConnection,
              onUpdateRef.current,
              (cfg) => ({
                ...cfg,
                boardMode: {
                  ...(cfg.boardMode ?? {}),
                  teamBoard: pick,
                  teamBoardDetails: details,
                  columnsToLoad: getDefaultColumnsToLoad(rawCols),
                  doneColumn: getDefaultDoneColumn(rawCols),
                  workItemTypes: [],
                  statusMappings: undefined,
                },
              }),
            );
          } catch {
            /* non-fatal */
          }
        }
      } catch {
        setBoards([]);
        setHasError(true);
      } finally {
        setIsLoadingBoards(false);
      }
    };
    void load();
  }, [
    isVisible,
    model.boardId,
    selectedAccountName,
    selectedProjectId,
    selectedTeamId,
    vstsApi,
  ]);

  useEffect(() => {
    const load = async (): Promise<void> => {
      const editingConnection = editingConnectionRef.current;
      if (
        !isVisible ||
        !model.boardId ||
        !editingConnection.id ||
        !vstsApi ||
        selectedAccountName === NONE_OPTION ||
        selectedMode !== STATUS_MODE
      ) {
        setStatusWorkItemTypes([]);
        return;
      }
      setIsLoadingStatusWorkItemTypes(true);
      setHasError(false);
      try {
        const current = configurationRef.current;
        const projectIds = (current.statusMode?.projects ?? [])
          .map((p) => p.id ?? "")
          .filter(Boolean);
        const response = await vstsApi.getProcessWorkItemTypes(
          model.boardId,
          editingConnection.id,
          selectedAccountName,
          projectIds,
        );
        setStatusWorkItemTypes(response ?? []);
      } catch {
        setStatusWorkItemTypes([]);
        setHasError(true);
      } finally {
        setIsLoadingStatusWorkItemTypes(false);
      }
    };
    void load();
  }, [
    isVisible,
    model.boardId,
    selectedAccountName,
    selectedMode,
    statusModeProjectIdsKey,
    vstsApi,
  ]);

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const update = (
    updater: (cfg: Partial<VstsConfiguration>) => Partial<VstsConfiguration>,
  ): void =>
    updateConnectionConfiguration<VstsConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      updater,
    );

  // ─── Return ───────────────────────────────────────────────────────────────

  return {
    isVisible,
    hasError,
    isLoadingAccounts,
    isLoadingProjects,
    isLoadingTeams,
    isLoadingBoards,
    isLoadingIdentityFields,
    isLoadingStatusWorkItemTypes,
    accounts,
    projects,
    teams,
    boards,
    columns,
    columnOptions,
    statusMappingColumns,
    workItemTypeNames,
    identityFields,
    statusWorkItemTypes,
    selectedAccountName,
    selectedMode,
    selectedProjectId,
    selectedTeamId,
    selectedBoardId,
    selectedColumnsToLoad,
    selectedDoneColumnKey,
    selectedDoneColumnLabel,
    selectedWorkItemTypeFilter,
    selectedIdentityFieldReferenceName,
    isBoardMode: selectedMode === BOARD_MODE,
    isStatusMode: selectedMode === STATUS_MODE,
    whenDoneAssignToCreator: configuration.whenDoneAssignToCreator ?? false,
    includeProjectInTitle: configuration.includeProjectInTitle ?? false,
    includeTypeAcronymInTitle: configuration.includeTypeAcronymInTitle ?? false,
    includeParentIdAsTag: configuration.includeParentIdAsTag ?? false,
    includeTags: configuration.includeTags ?? false,
    statusModeProjects,
    workItemTypeMappings,
    noneOptionValue: NONE_OPTION,
    accountQuery,
    projectQuery,
    teamQuery,
    boardQuery,
    statusModeProjectsQuery,
    workItemTypeFilterQuery,
    columnsToLoadQuery,
    doneColumnQuery,
    identityFieldQuery,
    setAccountQuery,
    setProjectQuery,
    setTeamQuery,
    setBoardQuery,
    setStatusModeProjectsQuery,
    setWorkItemTypeFilterQuery,
    setColumnsToLoadQuery,
    setDoneColumnQuery,
    setIdentityFieldQuery,

    onSelectAccount: (optionValue?: string) => {
      const selected = String(optionValue ?? NONE_OPTION);
      setAccountQuery("");
      update((cfg) => {
        if (selected === NONE_OPTION) {
          return {
            ...cfg,
            account: undefined,
            boardMode: undefined,
            assignedToField: undefined,
          };
        }
        const acc = accounts.find((a) => a.accountName === selected);
        if (!acc) return cfg;
        return {
          ...cfg,
          mode: BOARD_MODE,
          account: acc,
          boardMode: cfg.boardMode ?? {},
          assignedToField: undefined,
        };
      });
    },

    onSelectMode: (mode: string) => {
      // A load failure from the mode being left (e.g. a board-mode team/board
      // fetch) has no bearing on the mode being entered. Teams/boards loaders
      // only re-run when their own inputs change, not on a mode switch, so
      // without this the stale error would otherwise be displayed forever.
      setHasError(false);
      update((cfg) => ({
        ...cfg,
        mode,
        statusMode:
          cfg.statusMode ?? ({} as Partial<VstsStatusModeConfiguration>),
      }));
    },

    onSelectProject: (optionValue?: string) => {
      const selected = String(optionValue ?? NONE_OPTION);
      setProjectQuery("");
      update((cfg) => {
        const cleared = clearBoardSelection(cfg.boardMode);
        if (selected === NONE_OPTION) {
          return {
            ...cfg,
            mode: BOARD_MODE,
            boardMode: { ...cleared, project: undefined, team: undefined },
          };
        }
        const proj = projects.find((p) => p.id === selected);
        if (!proj) return cfg;
        return {
          ...cfg,
          mode: BOARD_MODE,
          boardMode: { ...cleared, project: proj, team: undefined },
        };
      });
    },

    onSelectTeam: (optionValue?: string) => {
      const selected = String(optionValue ?? NONE_OPTION);
      setTeamQuery("");
      update((cfg) => {
        const bm = cfg.boardMode ?? {};
        if (selected === NONE_OPTION) {
          return {
            ...cfg,
            boardMode: { ...bm, ...clearBoardSelection(), team: undefined },
          };
        }
        const team = teams.find((t) => t.id === selected);
        if (!team) return cfg;
        return { ...cfg, boardMode: { ...bm, ...clearBoardSelection(), team } };
      });
    },

    onSelectBoard: async (optionValue?: string): Promise<void> => {
      const selected = String(optionValue ?? NONE_OPTION);
      setBoardQuery("");
      if (selected === NONE_OPTION) {
        update((cfg) => ({
          ...cfg,
          boardMode: { ...(cfg.boardMode ?? {}), ...clearBoardSelection() },
        }));
        return;
      }
      const board = boards.find((b) => b.id === selected);
      if (
        !board ||
        !vstsApi ||
        !model.boardId ||
        !model.editingConnection.id ||
        selectedAccountName === NONE_OPTION ||
        selectedProjectId === NONE_OPTION ||
        selectedTeamId === NONE_OPTION
      ) {
        return;
      }
      try {
        setHasError(false);
        const details = await vstsApi.getTeamBoardDetails(
          model.boardId,
          model.editingConnection.id,
          selectedAccountName,
          selectedProjectId,
          selectedTeamId,
          board.id,
        );
        const rawCols = (details.columns ?? []) as VstsBoardColumn[];
        update((cfg) => {
          const bm = cfg.boardMode ?? {};
          // Existing selections only survive re-selecting the same board.
          // Columns from a different board do not exist in the new details, so
          // keeping them would leave selections that the pickers cannot show.
          const isSameBoard = bm.teamBoard?.id === board.id;
          const keptColumns = isSameBoard
            ? reconcileVstsColumns(bm.columnsToLoad ?? [], rawCols)
            : [];
          const keptDoneColumn =
            isSameBoard && bm.doneColumn
              ? findVstsColumnByKey(rawCols, getVstsColumnKey(bm.doneColumn))
              : undefined;
          return {
            ...cfg,
            boardMode: {
              ...bm,
              teamBoard: board,
              teamBoardDetails: details,
              columnsToLoad:
                keptColumns.length > 0
                  ? keptColumns
                  : getDefaultColumnsToLoad(rawCols),
              doneColumn: keptDoneColumn ?? getDefaultDoneColumn(rawCols),
              workItemTypes: isSameBoard ? (bm.workItemTypes ?? []) : [],
              statusMappings: isSameBoard ? bm.statusMappings : undefined,
            },
          };
        });
      } catch {
        setHasError(true);
      }
    },

    onSelectColumnsToLoad: (columnKeys: string[]) => {
      setColumnsToLoadQuery("");
      update((cfg) => {
        const allCols = getColumns(cfg);
        return {
          ...cfg,
          boardMode: {
            ...(cfg.boardMode ?? {}),
            columnsToLoad: allCols.filter((c) =>
              columnKeys.includes(getVstsColumnKey(c)),
            ),
          },
        };
      });
    },

    onSelectDoneColumn: (columnKey?: string) => {
      setDoneColumnQuery("");
      update((cfg) => {
        const allCols = getColumns(cfg);
        return {
          ...cfg,
          boardMode: {
            ...(cfg.boardMode ?? {}),
            doneColumn:
              columnKey && columnKey !== NONE_OPTION
                ? findVstsColumnByKey(allCols, columnKey)
                : undefined,
          },
        };
      });
    },

    onSelectWorkItemTypeFilter: (typeNames: string[]) => {
      setWorkItemTypeFilterQuery("");
      update((cfg) => ({
        ...cfg,
        boardMode: { ...(cfg.boardMode ?? {}), workItemTypes: typeNames },
      }));
    },

    onSelectIdentityField: (referenceName?: string) => {
      setIdentityFieldQuery("");
      update((cfg) => {
        if (!referenceName || referenceName === NONE_OPTION) {
          return { ...cfg, assignedToField: undefined };
        }
        const field = identityFields.find(
          (f) => f.referenceName === referenceName,
        );
        return field ? { ...cfg, assignedToField: field } : cfg;
      });
    },

    onToggleWhenDoneAssignToCreator: (checked: boolean) =>
      update((cfg) => ({ ...cfg, whenDoneAssignToCreator: checked })),

    onToggleIncludeProjectInTitle: (checked: boolean) =>
      update((cfg) => ({ ...cfg, includeProjectInTitle: checked })),

    onToggleIncludeTypeAcronymInTitle: (checked: boolean) =>
      update((cfg) => ({ ...cfg, includeTypeAcronymInTitle: checked })),

    onToggleIncludeParentIdAsTag: (checked: boolean) =>
      update((cfg) => ({ ...cfg, includeParentIdAsTag: checked })),

    onToggleIncludeTags: (checked: boolean) =>
      update((cfg) => ({ ...cfg, includeTags: checked })),

    onSelectStatusModeProjects: (projectIds: string[]) => {
      setStatusModeProjectsQuery("");
      update((cfg) => {
        const selected = projects.filter((p) =>
          projectIds.includes(p.id ?? ""),
        );
        return {
          ...cfg,
          statusMode: { ...(cfg.statusMode ?? {}), projects: selected },
        };
      });
    },

    onAddWorkItemTypeMapping: (workItemType: VstsProcessWorkItemType) => {
      update((cfg) => {
        const mappings = cfg.statusMode?.workItemTypeMappings ?? [];
        if (mappings.some((m) => m.workItemType.url === workItemType.url))
          return cfg;
        const states = (workItemType.states ?? []) as VstsWorkItemState[];
        const newMapping: VstsWorkItemTypeMapping = {
          workItemType,
          statusesToLoad: states.filter(
            (s) =>
              s.stateCategory !== "Completed" && s.stateCategory !== "Removed",
          ),
          doneStatus:
            states.find((s) => s.stateCategory === "Completed") ?? null,
        };
        return {
          ...cfg,
          statusMode: {
            ...(cfg.statusMode ?? {}),
            workItemTypeMappings: [...mappings, newMapping],
          },
        };
      });
    },

    onRemoveWorkItemTypeMapping: (index: number) => {
      update((cfg) => ({
        ...cfg,
        statusMode: {
          ...(cfg.statusMode ?? {}),
          workItemTypeMappings: (
            cfg.statusMode?.workItemTypeMappings ?? []
          ).filter((_, i) => i !== index),
        },
      }));
    },

    onSetMappingStatusesToLoad: (index: number, stateNames: string[]) => {
      update((cfg) => {
        const mappings = [...(cfg.statusMode?.workItemTypeMappings ?? [])];
        const m = mappings[index];
        if (!m) return cfg;
        const states = (m.workItemType.states ?? []) as VstsWorkItemState[];
        mappings[index] = {
          ...m,
          statusesToLoad: states.filter((s) =>
            stateNames.includes(s.name ?? ""),
          ),
        };
        return {
          ...cfg,
          statusMode: {
            ...(cfg.statusMode ?? {}),
            workItemTypeMappings: mappings,
          },
        };
      });
    },

    onSetMappingDoneStatus: (index: number, stateName?: string) => {
      update((cfg) => {
        const mappings = [...(cfg.statusMode?.workItemTypeMappings ?? [])];
        const m = mappings[index];
        if (!m) return cfg;
        const states = (m.workItemType.states ?? []) as VstsWorkItemState[];
        mappings[index] = {
          ...m,
          doneStatus: stateName
            ? (states.find((s) => s.name === stateName) ?? null)
            : null,
        };
        return {
          ...cfg,
          statusMode: {
            ...(cfg.statusMode ?? {}),
            workItemTypeMappings: mappings,
          },
        };
      });
    },

    onSetMappingStatusMappings: (
      index: number,
      statusMappings: SourceStatusMapping[],
    ) => {
      update((cfg) => {
        const mappings = [...(cfg.statusMode?.workItemTypeMappings ?? [])];
        const m = mappings[index];
        if (!m) return cfg;
        mappings[index] = { ...m, statusMappings };
        return {
          ...cfg,
          statusMode: {
            ...(cfg.statusMode ?? {}),
            workItemTypeMappings: mappings,
          },
        };
      });
    },

    onSetBoardModeStatusMappings: (statusMappings: SourceStatusMapping[]) => {
      update((cfg) => ({
        ...cfg,
        boardMode: { ...(cfg.boardMode ?? {}), statusMappings },
      }));
    },
  };
};
